/* global React, Icon, PhoneMockup */

// Phase B: Hero reads metric values from window.HEALBOT_HERO_METRICS (source
// of truth in src/data/heroMetrics.js). When `value === null` we fall back
// to the literal "X" so the page never goes blank if the data file fails
// to load — the placeholder regression test (`tests/check-placeholders.mjs`)
// catches that case in CI before it reaches a deploy.
//
// Tag swap: when real data is present, the `<span className="tag">` text
// flips from `hero.metric.N.tag` ("placeholder") to `hero.metric.N.tag.verified`
// ("підтверджено" / "verified" / etc.). One condition per tile so a partial
// data load (e.g. only bookingsUplift filled) still renders correctly.
//
// CTA upgrade: the secondary CTA `<a href="#case">` is upgraded to deep-link
// into `/case/{slug}/` (per locale path) ONLY when both
// `window.__HB_CASE_STUDY_ENABLED__` is true AND a slug is configured. Until
// the rollout flag flips, the legacy `#case` anchor is preserved so the
// scroll-to-section behaviour keeps working.
//
// Architecture: file ends with `window.Hero = Hero;` (no hooks added in this
// pass — the metric read is synchronous through window globals). Per
// `.ai-factory/ARCHITECTURE.md` §"Ключевые принципы".

function Hero() {
  const t = (k) => window.i18n.t(k);

  // Read locale once per render; locale changes trigger an App-level re-render
  // through the `i18n:change` listener already present in src/App.jsx, so the
  // formatter below picks up the new BCP-47 tag automatically.
  const locale = (window.i18n && window.i18n.getLocale && window.i18n.getLocale()) || "uk";
  const numberLocale =
    (window.i18n && window.i18n.numberLocale && window.i18n.numberLocale(locale)) || "uk-UA";

  // Helper: locale-correct number formatting (1 234 vs 1,234) for the metric
  // tile. Returns the raw "X" sentinel when value is missing — this is the
  // ship-gate signal for `tests/check-placeholders.mjs`.
  const fmt = (value) => {
    if (value == null || typeof value !== "number" || Number.isNaN(value)) return "X";
    try {
      return new Intl.NumberFormat(numberLocale).format(value);
    } catch (_e) {
      return String(value);
    }
  };

  const metrics = (typeof window !== "undefined" && window.HEALBOT_HERO_METRICS) || {};
  const m1 = metrics.bookingsUplift || {};
  const m2 = metrics.avgReplyTime || {};
  const m3 = metrics.frontDeskHours || {};

  // Tag: "placeholder" until value lands, then "verified" (per A.4 in plan).
  // Each tile decides independently so a partial fill is rendered correctly.
  const tagFor = (n, value) =>
    value == null ? t("hero.metric." + n + ".tag") : t("hero.metric." + n + ".tag.verified");

  // Показываем только плитки с реальным значением. Пока цифр нет (все три
  // value === null в src/data/heroMetrics.js), блок метрик не рисуется вовсе.
  // Раньше он выводил «X%» с бейджем «placeholder» и отдельной строкой-
  // инструкцией редактору — её видел каждый посетитель прода, в английской
  // версии дословно «replace placeholders with real numbers before launch».
  // Частичное заполнение поддерживается: придут не все три — покажем те, что есть.
  const filledMetrics = [
    { n: 1, value: m1.value, unit: "%" },
    { n: 2, value: m2.value, unit: t("hero.metric.2.unit") },
    { n: 3, value: m3.value, unit: t("hero.metric.3.unit") },
  ].filter((m) => m.value != null);

  // Case-study deep link. Defaults: flag off → keep legacy `#case` anchor
  // (preserves scroll behaviour for users with bookmarks pointing at #case).
  // Flag on → root-relative `/case/{slug}/` per locale (the per-locale path
  // is added by the locale prefix because the URL is interpreted by nginx,
  // not by client-side routing).
  const caseStudyEnabled =
    typeof window !== "undefined" && window.__HB_CASE_STUDY_ENABLED__ === true;
  const slug =
    (typeof window !== "undefined" &&
      window.HEALBOT_CASE_STUDY &&
      window.HEALBOT_CASE_STUDY.slug) ||
    null;

  // Per-locale case URL prefix: uk = no prefix (root-implicit), other locales
  // get their /xx/ prefix. Mirrors the convention used by scripts/build-locale-html.js.
  const localePrefix = locale === "uk" ? "" : "/" + locale;
  const caseHref = caseStudyEnabled && slug ? localePrefix + "/case/" + slug + "/" : "#case";

  // Optional analytics shim. window.analytics may be wired up later (per the
  // separate "Форма збору лідів + аналітика" milestone). Until then this is
  // a no-op safe call. Console-logged only when __HB_DEBUG__ is on, so prod
  // bundles stay quiet.
  const trackCaseClick = () => {
    try {
      if (window.analytics && typeof window.analytics.track === "function") {
        window.analytics.track("case_study_cta_click", { source: "hero", slug: slug || null });
      }
      if (window.__HB_DEBUG__) {
        // eslint-disable-next-line no-console
        console.debug("[hero] case_study_cta_click", { source: "hero", slug });
      }
    } catch (_e) {
      // Never let analytics break the navigation — fall through.
    }
  };

  // Debug: log once per render whether real data is present and which locale
  // formatter is selected. Gated so it never spams prod consoles.
  if (typeof window !== "undefined" && window.__HB_DEBUG__) {
    // eslint-disable-next-line no-console
    console.debug("[hero] render", {
      locale,
      numberLocale,
      hasData: m1.value != null || m2.value != null || m3.value != null,
      caseStudyEnabled,
      caseHref,
    });
  }

  return (
    <section className="hero">
      <div className="container hero-grid">
        <div className="hero-copy">
          <span className="chip">
            <span className="dot"></span>
            {t("hero.chip")}
          </span>
          <h1>
            {t("hero.h1.line1")}
            <br />
            {t("hero.h1.line2a")} <em>{t("hero.h1.line2b")}</em>
            <br />
            {t("hero.h1.line3")}
          </h1>
          <p className="hero-lede">{t("hero.lede")}</p>
          <div className="hero-ctas">
            <a href="#cta" className="btn btn-primary btn-lg">
              {t("hero.cta.primary")} <Icon name="arrow" size={16} />
            </a>
            {/* Ghost-pill в паре с чернильной primary: Steep-правило «filled + ghost». */}
            <a href={caseHref} className="btn btn-ghost btn-lg" onClick={trackCaseClick}>
              <Icon name="play" size={14} color="var(--coral)" />
              {t("hero.cta.secondary")}
            </a>
          </div>
          {/*
            aria-live="polite" — when the locale switches and numbers reformat
            (1 234 → 1,234), screen readers announce the change. Verified by
            the axe-core scan in tests/case-study-a11y.spec.html (Phase F.5).
          */}
          {filledMetrics.length > 0 && (
            <div className="hero-metrics" aria-label={t("hero.metrics.aria")} aria-live="polite">
              {filledMetrics.map((m) => (
                <div className="metric" key={m.n}>
                  <span className="tag">{tagFor(m.n, m.value)}</span>
                  <div className="val">
                    {fmt(m.value)}
                    <span className="unit">{m.unit}</span>
                  </div>
                  <div className="lbl">
                    {t("hero.metric." + m.n + ".label")}
                    <br />
                    {t("hero.metric." + m.n + ".label.sub")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="hero-stage">
          <div className="hero-stage-deco"></div>
          <PhoneMockup variant="hero" />
          <div className="hero-video-card">
            <div className="ratio">
              <div className="play">
                <div className="play-btn">
                  <Icon name="play" size={22} color="var(--coral)" />
                </div>
              </div>
            </div>
            <div className="meta">
              <div>
                <strong>{t("hero.video.title")}</strong>
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                  {t("hero.video.meta")}
                </div>
              </div>
              <span className="dur">0:60</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

window.Hero = Hero;
